import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Layout,
  Card,
  Form,
  Input,
  Button,
  Typography,
  message,
  Space,
  Divider,
  Row,
  Col,
  InputNumber,
  Alert,
  Steps
} from 'antd';
import {
  UserOutlined,
  LockOutlined,
  MailOutlined,
  BankOutlined,
  NumberOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { authAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import LoadingAnimation from '../components/LoadingAnimation';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

interface RegistrationData {
  email: string;
  password: string;
  confirmPassword: string;
  name: string;
  companyName: string;
  requestQuota: number;
}

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [registrationData, setRegistrationData] = useState<RegistrationData>({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    companyName: '',
    requestQuota: 100000
  });

  const [form] = Form.useForm();

  const quotaOptions = [
    { value: 10000, label: '10,000 requests', price: '$49/month' },
    { value: 50000, label: '50,000 requests', price: '$199/month' },
    { value: 100000, label: '100,000 requests', price: '$349/month' },
    { value: 500000, label: '500,000 requests', price: '$1,499/month' },
    { value: 1000000, label: '1,000,000 requests', price: '$2,499/month' },
  ];

  const handleRegister = async (values: any) => {
    try {
      setLoading(true);
      
      // Merge data from all steps
      const allData = { ...registrationData, ...values };
      
      // Prepare registration payload
      const registrationPayload = {
        companyName: allData.companyName,
        adminName: allData.name,
        email: allData.email,
        password: allData.password,
        confirmPassword: allData.confirmPassword,
        quota: allData.requestQuota
      };

      console.log('Registering with payload:', registrationPayload);

      // Call registration API
      const response = await authAPI.register(registrationPayload);
      
      if (response.data.success) {
        message.success('Registration successful! Welcome to Texo.');
        setCurrentStep(2); // Show success step
        
        // Automatically log in the user with the returned token
        login(response.data.token, response.data.user);
        
        // Redirect to dashboard after a delay
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        message.error(response.data.message || 'Registration failed');
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      message.error(
        error.response?.data?.message || 
        error.response?.data?.error || 
        'Registration failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    form.validateFields().then((values) => {
      const updatedData = { ...registrationData, ...values };
      setRegistrationData(updatedData);
      setCurrentStep(currentStep + 1);
      
      // Set form fields for the next step
      form.setFieldsValue(updatedData);
    }).catch((errorInfo) => {
      console.log('Validation failed:', errorInfo);
    });
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
    // Restore form fields for the previous step
    form.setFieldsValue(registrationData);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div>
            <Title level={3} style={{ textAlign: 'center', marginBottom: '32px', color: '#0f172a', fontWeight: 600 }}>
              Company Information
            </Title>
            <Form.Item
              name="companyName"
              rules={[
                { required: true, message: 'Please enter your company name' },
                { min: 2, message: 'Company name must be at least 2 characters' }
              ]}
            >
              <Input
                size="large"
                prefix={<BankOutlined />}
                placeholder="Company Name"
                style={{ borderRadius: '8px' }}
              />
            </Form.Item>

            <Form.Item
              name="name"
              rules={[
                { required: true, message: 'Please enter your full name' },
                { min: 2, message: 'Name must be at least 2 characters' }
              ]}
            >
              <Input
                size="large"
                prefix={<UserOutlined />}
                placeholder="Your Full Name"
                style={{ borderRadius: '8px' }}
              />
            </Form.Item>

            <Form.Item
              name="email"
              rules={[
                { required: true, message: 'Please enter your email' },
                { type: 'email', message: 'Please enter a valid email address' }
              ]}
            >
              <Input
                size="large"
                prefix={<MailOutlined />}
                placeholder="Company Email Address"
                style={{ borderRadius: '8px' }}
              />
            </Form.Item>

            <Form.Item style={{ marginTop: '32px' }}>
              <Button
                type="primary"
                size="large"
                onClick={nextStep}
                style={{ 
                  width: '100%', 
                  borderRadius: '8px',
                  height: '48px',
                  fontSize: '16px',
                  background: '#1e40af',
                  borderColor: '#1e40af',
                  fontWeight: 600
                }}
              >
                Next Step
              </Button>
            </Form.Item>
          </div>
        );

      case 1:
        return (
          <div>
            <Title level={3} style={{ textAlign: 'center', marginBottom: '16px', color: '#0f172a', fontWeight: 600 }}>
              Account Setup & Subscription
            </Title>
            <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginBottom: '32px', fontSize: '15px' }}>
              Set your password and choose your request quota
            </Text>

            <Form.Item
              name="password"
              rules={[
                { required: true, message: 'Please enter your password' },
                { min: 8, message: 'Password must be at least 8 characters' },
                { pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, message: 'Password must contain at least one uppercase letter, one lowercase letter, and one number' }
              ]}
            >
              <Input.Password
                size="large"
                prefix={<LockOutlined />}
                placeholder="Password"
                style={{ borderRadius: '8px' }}
              />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              dependencies={['password']}
              rules={[
                { required: true, message: 'Please confirm your password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}
            >
              <Input.Password
                size="large"
                prefix={<LockOutlined />}
                placeholder="Confirm Password"
                style={{ borderRadius: '8px' }}
              />
            </Form.Item>

            <Divider>Request Quota</Divider>

            <Form.Item
              name="requestQuota"
              label="Monthly Request Limit"
              rules={[
                { required: true, message: 'Please select your request quota' },
                { type: 'number', min: 1000, message: 'Minimum 1,000 requests required' }
              ]}
              initialValue={100000}
            >
              <InputNumber
                size="large"
                prefix={<NumberOutlined />}
                placeholder="Number of requests per month"
                style={{ 
                  width: '100%', 
                  borderRadius: '8px'
                }}
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => {
                  const parsed = parseInt(value?.replace(/\$\s?|(,*)/g, '') || '0');
                  return parsed as 1000 | 10000000;
                }}
                min={1000}
                max={10000000}
                step={1000}
              />
            </Form.Item>

            <Alert
              message="Pricing Information"
              description="This is a demo registration. In production, you would be redirected to payment processing after registration."
              type="info"
              style={{ marginBottom: '24px', borderRadius: '8px' }}
            />

            <Row gutter={16} style={{ marginTop: '32px' }}>
              <Col span={12}>
                <Button
                  size="large"
                  onClick={prevStep}
                  style={{ 
                    width: '100%', 
                    borderRadius: '8px',
                    height: '48px'
                  }}
                >
                  Previous
                </Button>
              </Col>
              <Col span={12}>
                <Button
                  type="primary"
                  size="large"
                  onClick={() => form.submit()}
                  loading={loading}
                  style={{ 
                    width: '100%', 
                    borderRadius: '8px',
                    height: '48px',
                    fontSize: '16px',
                    background: '#1e40af',
                    borderColor: '#1e40af',
                    fontWeight: 600
                  }}
                >
                  {loading ? 'Creating Account...' : 'Complete Registration'}
                </Button>
              </Col>
            </Row>
          </div>
        );

      case 2:
        return (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <CheckCircleOutlined style={{ fontSize: '72px', color: '#52c41a', marginBottom: '24px' }} />
            <Title level={2} style={{ color: '#1e40af', marginBottom: '16px' }}>
              Welcome to Texo!
            </Title>
            <Paragraph style={{ fontSize: '16px', marginBottom: '32px', color: '#475569' }}>
              Your company account has been created successfully. You are now logged in and will be redirected to the dashboard.
            </Paragraph>
            <LoadingAnimation type="pulse" message="Taking you to the dashboard..." />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Layout style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)' }}>
      <Content style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        padding: '24px'
      }}>
        <Card
          style={{
            width: '100%',
            maxWidth: '500px',
            borderRadius: '16px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            border: 'none'
          }}
        >
          <div style={{ padding: '20px' }}>
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <div style={{ 
                fontSize: '56px',
                fontWeight: '800',
                letterSpacing: '-0.5px',
                color: '#1e40af',
                marginBottom: '16px'
              }}>
                TEXO
              </div>
              <Text type="secondary" style={{ fontSize: '16px', display: 'block' }}>
                Create your company account
              </Text>
            </div>

            {/* Progress Steps */}
            {currentStep < 2 && (
              <Steps current={currentStep} style={{ marginBottom: '40px' }}>
                <Step title="Company Info" icon={<BankOutlined />} />
                <Step title="Account Setup" icon={<UserOutlined />} />
              </Steps>
            )}

            {/* Registration Form */}
            <Form
              form={form}
              layout="vertical"
              onFinish={handleRegister}
              autoComplete="off"
              initialValues={registrationData}
            >
              {renderStepContent()}
            </Form>

            {/* Footer */}
            {currentStep < 2 && (
              <div style={{ textAlign: 'center', marginTop: '32px' }}>
                <Divider>
                  <Text type="secondary">Already have an account?</Text>
                </Divider>
                <Link to="/login">
                  <Button type="link" size="large" icon={<ArrowLeftOutlined />}>
                    Back to Login
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </Card>
      </Content>
    </Layout>
  );
};

export default RegisterPage;
